// Cheque (برگ چک) printing: fields placed at fixed positions (mm) on the
// cheque leaf. A scanned cheque image is shown under the fields in the
// designer as a guide only; it is never part of the printed output.
//
//   renderCheque({ template, fields, cheques, preview, selected }) → { html, pageCount }
//
// fields:  [{ key, label, value(cheque) }]   — what can be printed
// cheques: [cheque, …]                        — one leaf (page) each
// template.fields[key]: { x, y, w, size, align, bold, visible, spacing, prefix, suffix }

import { fontById, fontFaceCss } from './fonts';

var FA = '۰۱۲۳۴۵۶۷۸۹';

function esc(v) {
  return String(v == null ? '' : v)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// Character spacing done by hand: browsers skip CSS letter-spacing on
// Persian (cursive) text, digits included. Letters of a word stay together so
// they keep joining; digits and symbols get the gap between them.
function spaced(text, mm) {
  if (!mm) return esc(text);
  var parts = String(text).match(/[\u0600-\u06EF\u06FA-\u06FF\u200C\u200D]+|[\s\S]/g) || [];
  // half the gap on each side: works whichever way the run is laid out
  var st = ' style="margin:0 ' + mm / 2 + 'mm"';
  return parts.map(function (p) {
    return '<span class="pc-ch"' + st + '>' + esc(p) + '</span>';
  }).join('');
}

export function chequeDigits(str, fa) {
  str = String(str == null ? '' : str);
  return fa ? str.replace(/[0-9]/g, function (d) { return FA[d]; }) : str;
}

// Default leaf: Sayad cheque body without the stub (~155 × 81 mm).
// Positions measured on the guide image (src/assets/print/cheque-guide.png).
export function baseChequeTemplate() {
  return {
    id: null,
    name: '',
    kind: 'cheque',
    builtIn: false,
    page: { w: 155, h: 81, offsetX: 0, offsetY: 0 }, // offset = printer feed correction
    guide: { show: true, w: 155, h: 81, x: 0, y: 0, opacity: 0.55 },
    theme: { fontId: 'vazirmatn', fontSize: 10, color: '#000000', faDigits: true },
    fields: {
      date: { x: 99, y: 8.6, w: 30, size: 11, align: 'center', bold: true, visible: true, spacing: 1.2 },
      dateWords: { x: 72, y: 18, w: 52, size: 9, align: 'start', bold: false, visible: true, spacing: 0 },
      amountWords: { x: 8, y: 27, w: 88, size: 9.5, align: 'start', bold: true, visible: true, spacing: 0 },
      payee: { x: 8, y: 35.5, w: 110, size: 10, align: 'start', bold: true, visible: true, spacing: 0 },
      payeeCode: { x: 30, y: 44, w: 70, size: 9.5, align: 'start', bold: false, visible: false, spacing: 1 },
      amount: { x: 2, y: 53, w: 48, size: 11, align: 'center', bold: true, visible: true, spacing: 0.4, prefix: '#', suffix: '#' },
      sayad: { x: 18, y: 2.5, w: 40, size: 9, align: 'center', bold: false, visible: false, spacing: 0.6 },
      description: { x: 8, y: 62, w: 90, size: 8.5, align: 'start', bold: false, visible: false, spacing: 0 },
    },
  };
}

function clone(o) {
  return JSON.parse(JSON.stringify(o));
}

export function normalizeCheque(tpl) {
  var b = baseChequeTemplate();
  var t = Object.assign(clone(b), clone(tpl || {}));
  t.page = Object.assign(b.page, t.page || {});
  t.guide = Object.assign(b.guide, t.guide || {});
  t.theme = Object.assign(b.theme, t.theme || {});
  var f = {};
  Object.keys(b.fields).forEach(function (k) {
    f[k] = Object.assign({}, b.fields[k], (tpl && tpl.fields && tpl.fields[k]) || {});
  });
  Object.keys((tpl && tpl.fields) || {}).forEach(function (k) {
    if (!f[k]) f[k] = tpl.fields[k];
  });
  t.fields = f;
  t.kind = 'cheque';
  return t;
}

export function builtInChequeTemplates() {
  var std = normalizeCheque({ id: 'chq-sayad', name: 'printTplChequeSayad', builtIn: true });
  var big = normalizeCheque({
    id: 'chq-sayad-large',
    name: 'printTplChequeLarge',
    builtIn: true,
    page: { w: 170, h: 85 },
    guide: { w: 170, h: 85 },
    theme: { fontSize: 11 },
  });
  // scale the large variant's positions with the leaf
  Object.keys(big.fields).forEach(function (k) {
    var f = big.fields[k];
    f.x = Math.round(f.x * (170 / 155) * 10) / 10;
    f.y = Math.round(f.y * (85 / 81) * 10) / 10;
    f.w = Math.round(f.w * (170 / 155) * 10) / 10;
  });
  return [std, big];
}

export function renderCheque({ template, fields, cheques, preview, guideUrl, selected }) {
  var t = template;
  var th = t.theme;
  var font = fontById(th.fontId);
  var list = cheques && cheques.length ? cheques : [{}];
  var pages = list.map(function (c) {
    var items = fields.map(function (def) {
      var f = t.fields[def.key];
      if (!f || (!f.visible && !preview)) return '';
      var v = def.value ? def.value(c) : c[def.key];
      if ((v == null || v === '') && !preview) return '';
      var empty = v == null || v === '';
      var text = chequeDigits(empty ? def.label : (f.prefix || '') + v + (f.suffix || ''), th.faDigits);
      return '<div class="pc-f' + (f.visible ? '' : ' pc-f--off') + (selected === def.key ? ' pc-f--sel' : '') +
        ((v == null || v === '') ? ' pc-f--empty' : '') + '" data-key="' + esc(def.key) + '" style="' +
        'right:auto;left:' + f.x + 'mm;top:' + f.y + 'mm;width:' + f.w + 'mm;font-size:' + f.size + 'pt;' +
        'text-align:' + ({ start: 'right', center: 'center', end: 'left' }[f.align] || 'right') + ';' +
        'font-weight:' + (f.bold ? 700 : 400) + '">' + spaced(text, Number(f.spacing) || 0) + '</div>';
    }).join('');
    var g = t.guide;
    var guide = preview && g.show && guideUrl
      ? '<img class="pc-guide" src="' + esc(guideUrl) + '" style="left:' + g.x + 'mm;top:' + g.y + 'mm;width:' + g.w + 'mm;height:' + g.h + 'mm;opacity:' + g.opacity + '" alt="">'
      : '';
    return '<section class="pc-page">' + guide + '<div class="pc-layer">' + items + '</div></section>';
  });
  var p = t.page;
  var css = [
    fontFaceCss(th.fontId),
    '@page{size:' + p.w + 'mm ' + p.h + 'mm;margin:0}',
    '*{box-sizing:border-box}',
    'html,body{margin:0;padding:0;background:' + (preview ? '#e9edf3' : '#fff') + '}',
    'body{font-family:' + font.family + ';color:' + th.color + ';-webkit-print-color-adjust:exact;print-color-adjust:exact}',
    '.pc-page{position:relative;width:' + p.w + 'mm;height:' + p.h + 'mm;overflow:hidden;background:#fff;margin:0 auto;' +
      (preview ? 'box-shadow:0 2px 10px rgba(15,23,42,.18);margin-bottom:6mm' : '') + '}',
    '.pc-page + .pc-page{break-before:page;page-break-before:always}',
    '.pc-guide{position:absolute;max-width:none;pointer-events:none;user-select:none}',
    '.pc-layer{position:absolute;inset:0;transform:translate(' + (p.offsetX || 0) + 'mm,' + (p.offsetY || 0) + 'mm)}',
    '.pc-ch{display:inline-block;white-space:pre}',
    '.pc-f{position:absolute;white-space:nowrap;overflow:hidden;line-height:1.25;direction:rtl;unicode-bidi:plaintext}',
    preview ? '.pc-f{cursor:move;outline:0.2mm dashed rgba(25,118,210,.55);background:rgba(25,118,210,.06);border-radius:0.6mm;user-select:none}' : '',
    preview ? '.pc-f:hover{background:rgba(25,118,210,.14)}' : '',
    preview ? '.pc-f--sel{outline:0.4mm solid #1976d2;background:rgba(25,118,210,.16)}' : '',
    preview ? '.pc-f--off{opacity:.35;outline-style:dotted}' : '',
    preview ? '.pc-f--empty{color:#64748b;font-style:italic}' : '',
    preview ? '.pc-f::after{content:"";position:absolute;left:0;top:0;bottom:0;width:1.6mm;cursor:ew-resize}' : '',
    '@media print{html,body{background:#fff}.pc-page{margin:0;box-shadow:none}}',
  ].join('\n');
  return {
    html: '<!DOCTYPE html><html dir="rtl" lang="fa"><head><meta charset="utf-8"><style>' + css + '</style></head><body>' + pages.join('') + '</body></html>',
    pageCount: pages.length,
  };
}
